import React, { Fragment, useState, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import API from "../../services/API";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from "reactstrap";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import dayjs from "dayjs";
import DataTable from "react-data-table-component";

const RMTProfile = () => {
  const [students, setStudents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState([]);

  const [registerModalOpen, setRegisterModalOpen] = useState(false);
  const [portalStudents, setPortalStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [search, setSearch] = useState("");
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTimeout, setSearchTimeout] = useState(null);

  const [presentStudents, setPresentStudents] = useState([]);
  const [absentStudents, setAbsentStudents] = useState([]);

  const getHeaders = () => ({
    "Content-Type": "application/json",
    Accept: "application/json",
    token: localStorage.getItem("token"),
  });

  const fetchRMTAttendanceToday = async (date) => {
    setLoading(true);

    const formattedDate = dayjs(date).format("YYYY-MM-DD");

    try {
      const response = await API.get(
        `/rmt/todayAttendance?date=${formattedDate}&is_portal=true`,
        { headers: getHeaders() }
      );

      if (response.data.response_code === 1000) {
        const data = response.data.data || {};
        setPresentStudents(data.rmt_present || []);
        setAbsentStudents(data.rmt_absent || []);
      } else {
        toast.error(response.data.message);
      }
    } catch {
      toast.error("Error fetching RMT attendance");
    } finally {
      setLoading(false);
    }
  };

  const fetchRMTAttendanceSummary = async (date) => {
    const monthParam = dayjs(date).format("YYYY-MM");

    try {
      const response = await API.get(
        `/rmt/attendanceSummary?month=${monthParam}&is_portal=true`,
        { headers: getHeaders() }
      );

      if (response.data.response_code === 1000) {
        setSummary(response.data.data.summary || []);
      }
    } catch {
      toast.error("Error fetching summary");
    }
  };

  const fetchPortalStudents = (page = 1, limit = 10, searchTerm = "") => {
    let url = `/portal/getStudents?page=${page}&limit=${limit}&is_portal=true`;

    if (searchTerm) {
      url += `&search=${encodeURIComponent(searchTerm)}`;
    }

    API.get(url, { headers: getHeaders() })
      .then((response) => {
        if (response.data.response_code === 1000) {
          const rmtUUIDs = new Set(students.map((s) => s.student_uuid));

          const filtered = (response.data.data.students || []).filter(
            (s) => !rmtUUIDs.has(s.student_uuid)
          );

          setPortalStudents(filtered);
          setTotalRows(response.data.data.total || 0);
        } else {
          toast.error(response.data.message);
        }
      })
      .catch(() => {
        toast.error("Error fetching students");
      });
  };

  const handleSearch = (e) => {
    const value = e.target.value;

    setSearch(value);
    setCurrentPage(1);

    if (searchTimeout) clearTimeout(searchTimeout);

    const timeout = setTimeout(() => {
      fetchPortalStudents(1, perPage, value);
    }, 400);

    setSearchTimeout(timeout);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchPortalStudents(page, perPage, search);
  };

  const handlePerPageChange = (newPerPage, page) => {
    setPerPage(newPerPage);
    fetchPortalStudents(page, newPerPage, search);
  };

  const registerRMTStudents = async () => {
    if (selectedStudents.length === 0) {
      toast.warning("Please select at least one student");
      return;
    }

    try {
      const response = await API.post(
        "/rmt/register?is_portal=true",
        { student_uuids: selectedStudents },
        { headers: getHeaders() }
      );

      if (response.data.response_code === 1000) {
        toast.success(response.data.message);

        setSelectedStudents([]);
        setSearch("");
        setPortalStudents([]);
        setRegisterModalOpen(false);

        setTimeout(() => {
          fetchRMTAttendanceToday(selectedDate);
          fetchRMTAttendanceSummary(selectedDate);
        }, 800);
      } else {
        toast.error(response.data.message);
      }
    } catch {
      toast.error("Error registering RMT students");
    }
  };

  const unregisterRMTStudents = async (uuid) => {
    try {
      const response = await API.post(
        "/rmt/unregister?is_portal=true",
        { student_uuids: [uuid] },
        { headers: getHeaders() }
      );

      if (response.data.response_code === 1000) {
        toast.success(response.data.message);

        setTimeout(() => {
          fetchRMTAttendanceToday(selectedDate);
          fetchRMTAttendanceSummary(selectedDate);
        }, 800);
      } else {
        toast.error(response.data.message);
      }
    } catch {
      toast.error("Error removing RMT student");
    }
  };

  const handleDownloadSummary = async () => {
    setLoading(true);

    try {
      const payload = {
        date: dayjs(selectedDate).format("YYYY-MM-DD"),
        filter_type: "month",
      };

      const response = await API.post(
        "/rmt/attendanceSummaryView?is_portal=true",
        payload,
        {
          headers: getHeaders(),
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], {
        type: "text/csv;charset=utf-8;",
      });

      const url = window.URL.createObjectURL(blob);
      const contentDisposition = response.headers["content-disposition"];

      let filename = `rmt_summary_${dayjs(selectedDate).format("YYYY_MM")}.csv`;

      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+)"?/);
        if (match && match[1]) filename = match[1];
      }

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);

      toast.success("Download started");
    } catch {
      toast.error("Failed to download summary");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRMTAttendanceToday(selectedDate);
  }, [selectedDate]);

  useEffect(() => {
    fetchRMTAttendanceSummary(selectedDate);
  }, [dayjs(selectedDate).format("YYYY-MM")]);

  useEffect(() => {
    fetchRMTAttendanceSummary(new Date());
  }, []);

  const selectedDateLabel = dayjs(selectedDate).format("DD MMM YYYY");
  const selectedMonthLabel = dayjs(selectedDate).format("MMMM YYYY");

  const totalRmtStudents = presentStudents.length + absentStudents.length;

  const attendanceRate = useMemo(() => {
    if (totalRmtStudents === 0) return 0;
    return Math.round((presentStudents.length / totalRmtStudents) * 100);
  }, [presentStudents.length, totalRmtStudents]);

  const getStudentName = (student) => {
    return (
      student.first_name ||
      student.moeis_name ||
      student.student_name ||
      "Unnamed Student"
    );
  };

  const getStudentInitials = (student) => {
    const name = getStudentName(student);

    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  };

  const tileContent = ({ date, view }) => {
    if (view !== "month") return null;

    const formatted = dayjs(date).format("YYYY-MM-DD");
    const rec = summary.find((item) => item.date === formatted);

    if (!rec) return null;

    const present = rec.present_count || 0;
    const total = rec.total_rmt_students || 0;
    const pct = rec.attendance_rate || 0;

    let bg = "#f8fafc";
    let color = "#475569";
    let border = "#e2e8f0";

    if (pct >= 90) {
      bg = "#ecfdf5";
      color = "#047857";
      border = "#bbf7d0";
    } else if (pct >= 75) {
      bg = "#eff6ff";
      color = "#1d4ed8";
      border = "#bfdbfe";
    } else if (pct >= 50) {
      bg = "#fefce8";
      color = "#a16207";
      border = "#fde68a";
    } else if (pct >= 25) {
      bg = "#fff7ed";
      color = "#c2410c";
      border = "#fed7aa";
    } else {
      bg = "#fef2f2";
      color = "#b91c1c";
      border = "#fecaca";
    }

    return (
      <div
        title={`Present: ${present} / ${total} (${pct}%)`}
        style={{
          background: bg,
          color,
          border: `1px solid ${border}`,
          padding: "3px 2px",
          fontSize: "0.7rem",
          lineHeight: 1.1,
          textAlign: "center",
          minWidth: "100%",
          fontWeight: 800,
        }}
      >
        {present}/{total}
      </div>
    );
  };

  const columns = [
    {
      name: "Student",
      selector: (row) => `${row.first_name || ""} (${row.moeis_name || "-"})`,
      sortable: true,
      grow: 2,
      cell: (row) => (
        <div className="py-2">
          <div className="fw-semibold text-dark">
            {row.first_name || row.moeis_name || "Unnamed Student"}
          </div>
          <div className="text-muted small">{row.moeis_name || "-"}</div>
        </div>
      ),
    },
    {
      name: "Class",
      selector: (row) => row.class_name || "Unassigned",
      sortable: true,
      center: true,
      cell: (row) => (
        <span
          className="badge rounded-pill px-3 py-2"
          style={{
            background: "#f1f5f9",
            color: "#334155",
            fontWeight: 700,
          }}
        >
          {row.class_name || "Unassigned"}
        </span>
      ),
    },
    {
      name: "Select",
      width: "110px",
      center: true,
      cell: (row) => (
        <input
          type="checkbox"
          checked={selectedStudents.includes(row.student_uuid)}
          onChange={() => {
            if (selectedStudents.includes(row.student_uuid)) {
              setSelectedStudents(
                selectedStudents.filter((id) => id !== row.student_uuid)
              );
            } else {
              setSelectedStudents([...selectedStudents, row.student_uuid]);
            }
          }}
        />
      ),
    },
  ];

  const modalTableStyles = {
    headRow: {
      style: {
        backgroundColor: "#f8fafc",
        borderBottom: "1px solid #e2e8f0",
      },
    },
    headCells: {
      style: {
        color: "#64748b",
        fontSize: "11px",
        fontWeight: 800,
        textTransform: "uppercase",
        letterSpacing: "0.08em",
      },
    },
    rows: {
      style: {
        borderBottom: "1px solid #f1f5f9",
      },
      highlightOnHoverStyle: {
        backgroundColor: "#f8fafc",
        outline: "none",
      },
    },
  };

  return (
    <Fragment>
      <div
        className="container-fluid"
        style={{
          background: "#f4f6fb",
          minHeight: "100vh",
          padding: "28px",
        }}
      >
        <div className="mx-auto" style={{ maxWidth: 1440 }}>
          {/* Header */}
          <div
            className="mb-4"
            style={{
              background: "#ffffff",
              border: "1px solid #dbe3ee",
              boxShadow: "0 18px 50px rgba(15, 23, 42, 0.06)",
            }}
          >
            <div
              className="d-flex flex-column flex-xl-row justify-content-between gap-4"
              style={{ padding: 26 }}
            >
              <div>
                <div
                  className="mb-2"
                  style={{
                    color: "#b91c1c",
                    fontSize: 11,
                    fontWeight: 900,
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                  }}
                >
                  RMT Aid Attendance Console
                </div>

                <h1
                  className="mb-2"
                  style={{
                    color: "#0f172a",
                    fontSize: 36,
                    fontWeight: 800,
                    letterSpacing: "-0.05em",
                    lineHeight: 1.05,
                  }}
                >
                  RMT Attendance
                </h1>

                <div
                  style={{
                    color: "#64748b",
                    maxWidth: 760,
                    fontSize: 14,
                    lineHeight: 1.6,
                  }}
                >
                  Monitor daily RMT attendance, review monthly meal-aid
                  participation, and manage registered aid recipients.
                </div>
              </div>

              <div className="d-flex flex-column flex-sm-row gap-2 align-items-start">
                <button
                  className="btn rounded-0 px-4 fw-semibold"
                  style={{
                    height: 42,
                    background: "#ffffff",
                    border: "1px solid #cbd5e1",
                    color: "#334155",
                  }}
                  onClick={handleDownloadSummary}
                  disabled={loading}
                >
                  Download CSV
                </button>

                <button
                  className="btn rounded-0 px-4 fw-semibold"
                  style={{
                    height: 42,
                    background: "#b91c1c",
                    border: "1px solid #991b1b",
                    color: "#ffffff",
                  }}
                  onClick={() => {
                    setRegisterModalOpen(true);
                    fetchPortalStudents(1, perPage, search);
                  }}
                  disabled={loading}
                >
                  Register Student
                </button>
              </div>
            </div>

            <div
              className="row g-0"
              style={{
                borderTop: "1px solid #e2e8f0",
              }}
            >
              <HeaderMetric
                label="Selected Date"
                value={selectedDateLabel}
                helper={selectedMonthLabel}
              />

              <HeaderMetric
                label="Present"
                value={presentStudents.length}
                helper="Recorded today"
                accent="#16a34a"
              />

              <HeaderMetric
                label="Absent / Unrecorded"
                value={absentStudents.length}
                helper="Requires attention"
                accent="#dc2626"
              />

              <HeaderMetric
                label="Attendance Rate"
                value={`${attendanceRate}%`}
                helper={`${presentStudents.length}/${totalRmtStudents} students`}
                accent="#2563eb"
              />
            </div>
          </div>

          {/* Main Grid */}
          <div className="row g-4 mb-4">
            {/* Calendar */}
            <div className="col-xl-5">
              <Panel
                title="Monthly Calendar"
                subtitle={`Attendance summary for ${selectedMonthLabel}`}
                action={
                  <div
                    style={{
                      background: "#f8fafc",
                      border: "1px solid #dbe3ee",
                      color: "#334155",
                      padding: "7px 10px",
                      fontSize: 12,
                      fontWeight: 800,
                    }}
                  >
                    {selectedMonthLabel}
                  </div>
                }
              >
                <div className="rmt-calendar-shell">
                  <Calendar
                    value={selectedDate}
                    onChange={(date) => setSelectedDate(date)}
                    onActiveStartDateChange={({ activeStartDate }) => {
                      if (activeStartDate) setSelectedDate(activeStartDate);
                    }}
                    tileContent={tileContent}
                  />
                </div>
              </Panel>
            </div>

            {/* Snapshot */}
            <div className="col-xl-7">
              <Panel
                title="Daily Attendance Snapshot"
                subtitle={`Selected date: ${selectedDateLabel}`}
                action={
                  <button
                    className="btn btn-sm rounded-0 fw-semibold"
                    style={{
                      border: "1px solid #dbe3ee",
                      background: "#ffffff",
                      color: "#334155",
                      padding: "7px 14px",
                    }}
                    onClick={() => fetchRMTAttendanceToday(selectedDate)}
                    disabled={loading}
                  >
                    Refresh
                  </button>
                }
              >
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-danger mb-3" />
                    <div className="fw-semibold text-dark">
                      Loading attendance records
                    </div>
                    <div className="text-muted small">
                      Fetching selected day attendance.
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="row g-3 mb-4">
                      <div className="col-md-6">
                        <SnapshotCard
                          title="Present"
                          value={presentStudents.length}
                          tone="#16a34a"
                          body="Students who have recorded valid RMT attendance."
                        />
                      </div>

                      <div className="col-md-6">
                        <SnapshotCard
                          title="Absent / Unrecorded"
                          value={absentStudents.length}
                          tone="#dc2626"
                          body="Students without recorded RMT attendance for this date."
                        />
                      </div>
                    </div>

                    <div
                      style={{
                        border: "1px solid #dbe3ee",
                        background: "#f8fafc",
                        padding: 20,
                      }}
                    >
                      <div className="d-flex justify-content-between mb-2">
                        <div
                          style={{
                            color: "#64748b",
                            fontSize: 12,
                            fontWeight: 800,
                            textTransform: "uppercase",
                            letterSpacing: "0.08em",
                          }}
                        >
                          Completion Rate
                        </div>

                        <div
                          style={{
                            color: "#0f172a",
                            fontWeight: 800,
                            fontSize: 13,
                          }}
                        >
                          {attendanceRate}%
                        </div>
                      </div>

                      <div
                        style={{
                          height: 10,
                          background: "#e2e8f0",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            height: "100%",
                            width: `${attendanceRate}%`,
                            background:
                              attendanceRate >= 75
                                ? "#16a34a"
                                : attendanceRate >= 50
                                ? "#f59e0b"
                                : "#dc2626",
                            transition: "width 0.25s ease",
                          }}
                        />
                      </div>

                      <div className="text-muted small mt-3">
                        {presentStudents.length} present out of{" "}
                        {totalRmtStudents} registered RMT students.
                      </div>
                    </div>
                  </>
                )}
              </Panel>
            </div>
          </div>

          {!loading && (
            <div className="row g-4">
              <div className="col-xl-6">
                <StudentCardSection
                  title="Present Students"
                  count={presentStudents.length}
                  tone="success"
                  students={presentStudents}
                  emptyText="No students present for this selected date."
                  unregisterRMTStudents={unregisterRMTStudents}
                  getStudentName={getStudentName}
                  getStudentInitials={getStudentInitials}
                />
              </div>

              <div className="col-xl-6">
                <StudentCardSection
                  title="Absent / Unrecorded"
                  count={absentStudents.length}
                  tone="danger"
                  students={absentStudents}
                  emptyText="No absent or unrecorded students for this selected date."
                  unregisterRMTStudents={unregisterRMTStudents}
                  getStudentName={getStudentName}
                  getStudentInitials={getStudentInitials}
                />
              </div>
            </div>
          )}
        </div>

        <style>
          {`
            .rmt-calendar-shell .react-calendar {
              width: 100%;
              border: none;
              font-family: inherit;
            }

            .rmt-calendar-shell .react-calendar__navigation {
              margin-bottom: 14px;
            }

            .rmt-calendar-shell .react-calendar__navigation button {
              color: #0f172a;
              font-weight: 800;
              background: #ffffff;
              border: 1px solid #dbe3ee;
              margin: 0 2px;
              min-height: 40px;
            }

            .rmt-calendar-shell .react-calendar__navigation button:hover {
              background: #f8fafc;
            }

            .rmt-calendar-shell .react-calendar__month-view__weekdays {
              color: #64748b;
              font-size: 11px;
              font-weight: 900;
              text-transform: uppercase;
              letter-spacing: 0.08em;
            }

            .rmt-calendar-shell .react-calendar__month-view__weekdays abbr {
              text-decoration: none;
            }

            .rmt-calendar-shell .react-calendar__tile {
              border: 1px solid #f1f5f9;
              min-height: 76px;
              background: #ffffff;
              color: #334155;
              padding: 8px 5px;
            }

            .rmt-calendar-shell .react-calendar__tile:hover {
              background: #f8fafc;
            }

            .rmt-calendar-shell .react-calendar__tile--active {
              background: #111827 !important;
              color: #ffffff !important;
            }

            .rmt-calendar-shell .react-calendar__tile--active > div {
              border-color: rgba(255,255,255,0.45) !important;
            }

            .rmt-calendar-shell .react-calendar__tile--now {
              background: #fff7ed;
            }
          `}
        </style>

        {/* Register Modal */}
        <Modal
          isOpen={registerModalOpen}
          toggle={() => setRegisterModalOpen(false)}
          size="lg"
          centered
        >
          <ModalHeader toggle={() => setRegisterModalOpen(false)}>
            <div>
              <div className="fw-bold text-dark">Register RMT Student</div>
              <div className="text-muted small">
                Select students to add into the RMT aid attendance list.
              </div>
            </div>
          </ModalHeader>

          <ModalBody style={{ background: "#f8fafc" }}>
            <div
              className="bg-white mb-3"
              style={{
                border: "1px solid #dbe3ee",
                padding: 16,
              }}
            >
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                <div>
                  <div className="fw-semibold text-dark">Student Directory</div>
                  <div className="text-muted small">
                    {selectedStudents.length} selected
                  </div>
                </div>

                <input
                  type="text"
                  className="form-control rounded-0"
                  style={{ maxWidth: 320 }}
                  placeholder="Search by name..."
                  value={search}
                  onChange={handleSearch}
                />
              </div>
            </div>

            <div className="bg-white" style={{ border: "1px solid #dbe3ee" }}>
              <DataTable
                columns={columns}
                data={portalStudents}
                pagination
                paginationServer
                paginationTotalRows={totalRows}
                paginationPerPage={perPage}
                paginationRowsPerPageOptions={[10, 20, 50]}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handlePerPageChange}
                highlightOnHover
                dense
                customStyles={modalTableStyles}
              />
            </div>
          </ModalBody>

          <ModalFooter>
            <Button
              color="light"
              className="rounded-0 px-4"
              onClick={() => setRegisterModalOpen(false)}
            >
              Cancel
            </Button>

            <Button
              color="danger"
              className="rounded-0 px-4"
              onClick={registerRMTStudents}
              disabled={selectedStudents.length === 0}
            >
              Register Selected
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </Fragment>
  );
};

const HeaderMetric = ({ label, value, helper, accent = "#b91c1c" }) => {
  return (
    <div
      className="col-md-3"
      style={{
        borderRight: "1px solid #e2e8f0",
        padding: 20,
      }}
    >
      <div
        style={{
          color: "#64748b",
          fontSize: 11,
          fontWeight: 900,
          textTransform: "uppercase",
          letterSpacing: "0.1em",
          marginBottom: 10,
        }}
      >
        {label}
      </div>

      <div
        style={{
          color: accent,
          fontSize: 28,
          fontWeight: 800,
          letterSpacing: "-0.04em",
          lineHeight: 1,
          marginBottom: 8,
        }}
      >
        {value}
      </div>

      <div className="text-muted small">{helper}</div>
    </div>
  );
};

const Panel = ({ title, subtitle, action, children }) => {
  return (
    <div
      className="h-100"
      style={{
        background: "#ffffff",
        border: "1px solid #dbe3ee",
        boxShadow: "0 14px 40px rgba(15, 23, 42, 0.05)",
      }}
    >
      <div
        className="d-flex justify-content-between align-items-start gap-3"
        style={{
          padding: 20,
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        <div>
          <h5
            className="mb-1"
            style={{
              color: "#0f172a",
              fontWeight: 800,
              letterSpacing: "-0.02em",
            }}
          >
            {title}
          </h5>

          <div className="text-muted small">{subtitle}</div>
        </div>

        {action}
      </div>

      <div style={{ padding: 20 }}>{children}</div>
    </div>
  );
};

const SnapshotCard = ({ title, value, body, tone }) => {
  return (
    <div
      className="h-100"
      style={{
        border: "1px solid #dbe3ee",
        background: "#ffffff",
        padding: 20,
      }}
    >
      <div className="d-flex justify-content-between align-items-start">
        <div>
          <div
            style={{
              color: "#64748b",
              fontSize: 12,
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              marginBottom: 10,
            }}
          >
            {title}
          </div>

          <div
            style={{
              color: tone,
              fontSize: 42,
              fontWeight: 800,
              letterSpacing: "-0.05em",
              lineHeight: 1,
            }}
          >
            {value}
          </div>
        </div>

        <div
          style={{
            width: 10,
            height: 10,
            background: tone,
          }}
        />
      </div>

      <div className="text-muted small mt-3">{body}</div>
    </div>
  );
};

const StudentCardSection = ({
  title,
  count,
  tone,
  students,
  emptyText,
  unregisterRMTStudents,
  getStudentName,
  getStudentInitials,
}) => {
  const accent = tone === "success" ? "#16a34a" : "#dc2626";
  const soft = tone === "success" ? "#ecfdf5" : "#fef2f2";

  return (
    <div
      style={{
        background: "#ffffff",
        border: "1px solid #dbe3ee",
        boxShadow: "0 14px 40px rgba(15, 23, 42, 0.05)",
      }}
    >
      <div
        className="d-flex justify-content-between align-items-start gap-3"
        style={{
          padding: 20,
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        <div>
          <h5
            className="mb-1"
            style={{
              color: "#0f172a",
              fontWeight: 800,
              letterSpacing: "-0.02em",
            }}
          >
            {title}
          </h5>

          <div className="text-muted small">
            {count} student{count === 1 ? "" : "s"} listed.
          </div>
        </div>

        <div
          style={{
            background: soft,
            color: accent,
            border: `1px solid ${tone === "success" ? "#bbf7d0" : "#fecaca"}`,
            padding: "7px 12px",
            fontWeight: 900,
          }}
        >
          {count}
        </div>
      </div>

      <div
        style={{
          padding: 16,
          maxHeight: 560,
          overflowY: "auto",
        }}
      >
        {students.length === 0 ? (
          <div
            className="text-center"
            style={{
              padding: 46,
              border: "1px dashed #cbd5e1",
              background: "#f8fafc",
            }}
          >
            <div className="fw-semibold text-dark mb-1">{emptyText}</div>
            <div className="text-muted small">
              Change the date or refresh attendance records.
            </div>
          </div>
        ) : (
          <div className="d-flex flex-column gap-2">
            {students.map((student) => (
              <div
                key={student.student_uuid}
                className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3"
                style={{
                  border: "1px solid #e2e8f0",
                  background: "#ffffff",
                  padding: 14,
                }}
              >
                <div className="d-flex align-items-center gap-3">
                  <div
                    style={{
                      width: 42,
                      height: 42,
                      background: soft,
                      color: accent,
                      border: `1px solid ${
                        tone === "success" ? "#bbf7d0" : "#fecaca"
                      }`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 900,
                      fontSize: 13,
                    }}
                  >
                    {getStudentInitials(student)}
                  </div>

                  <div>
                    <div className="fw-semibold text-dark">
                      {getStudentName(student)}
                    </div>

                    <div className="text-muted small">
                      {student.moeis_name || student.student_uuid || "-"}
                    </div>
                  </div>
                </div>

                <div className="d-flex flex-column flex-md-row align-items-md-center gap-3">
                  <div className="text-md-end">
                    <div
                      style={{
                        color: accent,
                        fontSize: 12,
                        fontWeight: 900,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                      }}
                    >
                      {tone === "success"
                        ? "Present"
                        : student.status === 0
                        ? "Absent"
                        : "Unrecorded"}
                    </div>

                    <div className="text-muted small">
                      {student.remarks || "No remarks"}
                    </div>
                  </div>

                  <Button
                    color="danger"
                    outline
                    size="sm"
                    className="rounded-0 px-3"
                    onClick={() => unregisterRMTStudents(student.student_uuid)}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RMTProfile;